import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api, { API_BASE_URL } from '../utils/api';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log('=== AUTH CONTEXT CHECK AUTH DEBUG ===');
      console.log('Token present:', !!token);
      console.log('Token value:', token ? token.substring(0, 20) + '...' : 'No token');
      
      if (token) {
        try {
          console.log('Fetching user profile...');
          const response = await api.get('/auth/profile');
          console.log('Profile response:', response.data);
          console.log('Profile response keys:', Object.keys(response.data));
          console.log('User object keys:', response.data.user ? Object.keys(response.data.user) : 'No user');
          console.log('Setting user to:', response.data.user);
          setUser(response.data.user);
          console.log('User set successfully:', response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          console.error('Error response:', error.response);
          console.error('Error status:', error.response?.status);
          console.error('Error data:', error.response?.data);
          logout();
        }
      } else {
        console.log('No token found, setting loading to false');
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  // Debug user state changes
  useEffect(() => {
    console.log('=== AUTH CONTEXT USER STATE CHANGE ===');
    console.log('User state changed to:', user);
    console.log('User ID:', user?._id);
    console.log('User email:', user?.email);
  }, [user]);

  const login = async (email, password) => {
    console.log('=== AUTH CONTEXT LOGIN DEBUG ===');
    console.log('AuthContext: Starting login process');
    console.log('AuthContext: API URL:', API_BASE_URL);
    console.log('AuthContext: Email:', email);
    console.log('AuthContext: Password length:', password ? password.length : 0);
    
    try {
      console.log('AuthContext: Making login request...');
      console.log('AuthContext: Full login URL:', `${API_BASE_URL}/api/auth/login`);
      
      const requestData = { email, password };
      console.log('AuthContext: Request data:', { email, password: '***' });
      
      const response = await api.post('/auth/login', requestData);
      
      console.log('AuthContext: Login response received:', response.data);
      console.log('AuthContext: Response status:', response.status);
      console.log('AuthContext: Response headers:', response.headers);
      
      const { token: newToken, user: userData } = response.data;
      console.log('AuthContext: Token received:', newToken ? 'YES' : 'NO');
      console.log('AuthContext: User data received:', userData ? 'YES' : 'NO');
      
      if (newToken) {
        console.log('AuthContext: Setting token and user...');
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
        console.log('AuthContext: Token and user set successfully');
        console.log('AuthContext: User data set:', userData);
        console.log('AuthContext: Token stored:', newToken ? 'YES' : 'NO');
      } else {
        console.log('AuthContext: No token received from response');
      }
      
      console.log('AuthContext: Login successful, returning success');
      return { success: true };
    } catch (error) {
      console.error('=== AUTH CONTEXT LOGIN ERROR ===');
      console.error('AuthContext: Login error:', error);
      console.error('AuthContext: Error message:', error.message);
      console.error('AuthContext: Error response:', error.response);
      console.error('AuthContext: Error status:', error.response?.status);
      console.error('AuthContext: Error data:', error.response?.data);
      
      // Handle rate limiting specifically
      if (error.response?.status === 429) {
        return { 
          success: false, 
          error: 'Too many login attempts. Please wait a few minutes before trying again.' 
        };
      }
      
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Login failed';
      console.error('AuthContext: Returning error message:', errorMessage);
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      // Split name into firstName and lastName
      const nameParts = name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const response = await api.post('/auth/register', {
        firstName,
        lastName,
        email,
        password
      });
      
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response);
      
      // Handle rate limiting specifically
      if (error.response?.status === 429) {
        return { 
          success: false, 
          error: 'Too many registration attempts. Please wait a few minutes before trying again.' 
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const updateProfile = async (profileData) => {
    try {
      console.log('AuthContext: updateProfile called with:', profileData);
      console.log('AuthContext: API URL:', `${API_BASE_URL}/api/user/profile`);
      
      const response = await api.put('/user/profile', profileData);
      
      console.log('AuthContext: Profile update response:', response.data);
      
      if (response.data.user) {
        setUser(response.data.user);
        console.log('AuthContext: User state updated with:', response.data.user);
        return { success: true, user: response.data.user };
      }
      
      console.log('AuthContext: No user data in response');
      return { success: false, error: 'Failed to update profile' };
    } catch (error) {
      console.error('AuthContext: Update profile error:', error);
      console.error('AuthContext: Error response:', error.response?.data);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to update profile' 
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 